import { useState, type FormEvent } from 'react';
import { MATCH_CODE_RE } from '../shared/constants';
import { Link, navigate } from '../lib/router';
import { forgetRecent, useRecent } from '../lib/store';
import { emptyDraft, resetDraft, useDraft } from '../lib/draft';

/** Accepts "RANG-7KQ2", "rang 7kq2" or just "7KQ2". */
export function normalizeCode(input: string): string | null {
  const raw = input.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/^RANG/, '');
  const code = `RANG-${raw}`;
  return MATCH_CODE_RE.test(code) ? code : null;
}

const STATUS_LABEL = { setup: 'Setting up', in_progress: 'In progress', finished: 'Finished' } as const;

export function Home() {
  const recent = useRecent();
  const draft = useDraft();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const hasDraft = draft.players.some((p) => p.name.trim());

  const open = (e: FormEvent) => {
    e.preventDefault();
    const c = normalizeCode(code);
    if (!c) return setError('Codes look like RANG-7KQ2.');
    navigate(`/m/${c}`);
  };

  return (
    <main className="mx-auto max-w-lg px-4 pt-[max(2rem,env(safe-area-inset-top))] pb-16">
      <header className="mb-8 text-center">
        <p className="eyebrow mb-3 text-gold-soft" aria-hidden>
          ♠ <span className="text-red-300">♥</span> ♣ <span className="text-red-300">♦</span>
        </p>
        <h1 className="font-display text-5xl leading-none font-extrabold tracking-tight">
          Rang
          <span className="block text-2xl font-semibold text-gold-soft">Scorekeeper</span>
        </h1>
        <p className="mt-3 text-cream/80">Seat the table, spin for the dealer, keep every deal honest.</p>
      </header>

      <div className="grid gap-3">
        <button
          type="button"
          className="btn btn-primary h-16 text-lg"
          onClick={() => {
            resetDraft(emptyDraft());
            navigate('/new');
          }}
        >
          ＋ New match
        </button>
        {hasDraft && (
          <Link href="/new" className="btn btn-ghost">
            Continue setting up
          </Link>
        )}

        <form onSubmit={open} className="panel mt-2 p-4" noValidate>
          <label htmlFor="code" className="mb-2 block font-semibold">
            Open a match
          </label>
          <div className="flex gap-2">
            <input
              id="code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError(null);
              }}
              placeholder="RANG-7KQ2"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'code-error' : undefined}
              className="field font-mono tracking-widest uppercase"
            />
            <button className="btn btn-ghost shrink-0">Open</button>
          </div>
          {error && (
            <p id="code-error" className="mt-2 text-sm text-red-300">
              {error}
            </p>
          )}
        </form>
      </div>

      <section className="mt-10" aria-labelledby="recent-h">
        <h2 id="recent-h" className="eyebrow mb-3 text-cream/70">
          Recent matches
        </h2>
        {recent.length === 0 ? (
          <p className="panel p-4 text-center text-cream/70">Matches you create or open will appear here.</p>
        ) : (
          <ul className="grid gap-2">
            {recent.map((r) => (
              <li key={r.code} className="card flex items-stretch overflow-hidden">
                <Link href={`/m/${r.code}`} className="flex flex-1 items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs font-bold tracking-wider text-ink-soft">{r.code}</p>
                    <p className="truncate font-semibold">
                      {r.teamA} <span className="text-ink-soft">vs</span> {r.teamB}
                    </p>
                    <p className="text-xs text-ink-soft">
                      {STATUS_LABEL[r.status]} · {r.dealsPlayed} {r.dealsPlayed === 1 ? 'deal' : 'deals'}
                    </p>
                  </div>
                  <span className="text-right" aria-label={`Guaa ${r.guaaA ?? 0} to ${r.guaaB ?? 0}`}>
                    <span className="block font-display text-2xl font-extrabold tabular-nums">
                      {r.guaaA ?? 0}–{r.guaaB ?? 0}
                    </span>
                    <span className="block text-[10px] font-bold tracking-wider text-ink-soft uppercase">Guaa</span>
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => forgetRecent(r.code)}
                  className="border-l border-ink/10 px-3 text-ink-soft hover:bg-ink/5"
                  aria-label={`Remove ${r.code} from recent matches`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
